import { useForm } from '@inertiajs/react'
import { MessageSquare } from 'lucide-react'
import { Button } from '@/components/ui/button'
import ProjectTaskCommentItem from '@/frontend/features/Fde/ProjectTaskCommentItem'
import { Textarea } from '@/components/ui/textarea'
import AgentWakeSelector from '@/frontend/features/Fde/AgentWakeSelector'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { FdeCopy, ProjectTaskAssignee, ProjectTaskComment } from '@/frontend/features/Fde/types'

interface Props {
  projectId: string
  taskId: string
  comments: ProjectTaskComment[]
  agents: ProjectTaskAssignee[]
  canManage: boolean
  copy: FdeCopy
}

export default function ProjectTaskComments({ projectId, taskId, comments, agents, canManage, copy }: Props) {
  const form = useForm<{ body: string; notify_agent_refs: string[] }>({ body: '', notify_agent_refs: [] })

  function submit() {
    form.transform((data) => ({ project_task_comment: data, project_task_id: taskId }))
    form.post(fdeProjectPath(fdeRoutes.projectTaskComments, projectId), {
      preserveScroll: true,
      preserveState: true,
      onSuccess: () => form.reset(),
    })
  }

  return (
    <section className="border-t border-border pt-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <MessageSquare className="size-4 text-muted-foreground" />
          {copy.management.comments}
        </h3>
        <span className="text-xs tabular-nums text-muted-foreground">{comments.length}</span>
      </div>
      <div className="space-y-5">
        {[...comments]
          .sort((left, right) => left.created_at.localeCompare(right.created_at))
          .map((comment) => (
            <ProjectTaskCommentItem
              key={comment.id}
              comment={comment}
              projectId={projectId}
              taskId={taskId}
              copy={copy}
            />
          ))}
      </div>
      {canManage && (
        <div className="mt-4 rounded-lg border border-border bg-background p-2 focus-within:ring-2 focus-within:ring-ring/20">
          <Textarea
            value={form.data.body}
            onChange={(event) => form.setData('body', event.target.value)}
            placeholder={copy.management.comment_placeholder}
            rows={3}
            className="resize-none border-0 px-2 shadow-none focus-visible:ring-0"
          />
          {agents.length > 0 && (
            <div className="mt-2">
              <AgentWakeSelector
                agents={agents}
                values={form.data.notify_agent_refs}
                label={copy.management.comment_notify}
                hint={copy.management.comment_notify_hint}
                emptyLabel={copy.management.comment_notify_empty}
                copy={copy.management}
                disabled={form.processing}
                onChange={(values) => form.setData('notify_agent_refs', values)}
              />
            </div>
          )}
          <div className="mt-2 flex justify-end">
            <Button size="sm" disabled={form.processing || !form.data.body.trim()} onClick={submit}>
              {copy.management.add_comment}
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
